/**
 * Self-synthesised UI sounds (WebAudio). No audio files: every role is a few oscillators or a
 * filtered noise burst, so nothing is borrowed from anywhere. Respects Settings → Sound effects.
 *
 * WOW · feel: every sound now plays through the shared SFX bus (volume slider + soft compressor) and
 * the big moments get a reverb "room" send and extra layers (sub thump, shimmer). Background music
 * lives in lib/music.ts (generative, per world).
 */
import { useApp } from '../state/store';
import { audioBuses } from './audioBus';
import { setMusicEnabled } from './music';

export type Sfx =
  | 'click'
  | 'disabled'
  | 'slider'
  | 'drawer'
  | 'whoosh'
  | 'magic'
  | 'cardAdded'
  | 'highlight'
  | 'circleFill'
  | 'sparkle'
  | 'chime'
  | 'complete'
  | 'celebrate'
  | 'skill'
  | 'unlock'
  | 'unitUnlock'
  | 'catchup'
  | 'refLibrary'
  | 'shutter'
  | 'pageTurn'
  | 'pop'
  | 'cheer'
  | 'confetti'
  | 'junoHello'
  | 'stepDone';

/** Where the recipe being played sends its sound (set by play()). */
let dest: AudioNode | null = null;
let wetSend: AudioNode | null = null;

function out(c: AudioContext, node: AudioNode) {
  node.connect(dest ?? c.destination);
  if (wetSend) node.connect(wetSend);
}

function tone(c: AudioContext, freq: number, start: number, dur: number, type: OscillatorType = 'sine', gain = 0.12, glideTo?: number) {
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = type;
  const t0 = c.currentTime + start;
  o.frequency.setValueAtTime(freq, t0);
  if (glideTo) o.frequency.exponentialRampToValueAtTime(glideTo, t0 + dur);
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  o.connect(g);
  out(c, g);
  o.start(t0);
  o.stop(t0 + dur + 0.05);
}

function noise(c: AudioContext, start: number, dur: number, from: number, to: number, gain = 0.1, q = 1.2) {
  const len = Math.max(1, Math.floor(c.sampleRate * dur));
  const buf = c.createBuffer(1, len, c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource();
  src.buffer = buf;
  const f = c.createBiquadFilter();
  f.type = 'bandpass';
  f.Q.value = q;
  const t0 = c.currentTime + start;
  f.frequency.setValueAtTime(from, t0);
  f.frequency.exponentialRampToValueAtTime(to, t0 + dur);
  const g = c.createGain();
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + dur * 0.3);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  src.connect(f).connect(g);
  out(c, g);
  src.start(t0);
}

const C5 = 523.25;
const E5 = 659.25;
const G5 = 783.99;
const C6 = 1046.5;

/**
 * Target length of each role (seconds), matched to the reference app's 18 UI sounds (report 02
 * §4.5) so every trigger keeps the same rhythm. Each recipe below ends within ±0.05 s of it.
 */
export const SFX_DURATION: Record<Sfx, number> = {
  click: 0.12,
  disabled: 0.39,
  slider: 0.13,
  drawer: 0.94,
  whoosh: 0.36,
  magic: 1.22,
  cardAdded: 0.24,
  highlight: 2.2,
  circleFill: 2.04,
  sparkle: 1.1,
  chime: 2.21,
  complete: 1.8,
  celebrate: 3.56,
  skill: 3.08,
  unlock: 2.2,
  unitUnlock: 4.84,
  catchup: 1.63,
  refLibrary: 2.44,
  shutter: 0.14,
  pageTurn: 0.35,
  pop: 0.18,
  cheer: 0.7,
  confetti: 0.9,
  junoHello: 0.62,
  stepDone: 0.5,
};

/** Reverb send per role (0 = dry). Big moments sound like they happen in a warm room. */
const WET: Partial<Record<Sfx, number>> = {
  magic: 0.35,
  highlight: 0.3,
  chime: 0.3,
  complete: 0.25,
  celebrate: 0.35,
  skill: 0.35,
  unlock: 0.25,
  unitUnlock: 0.4,
  junoHello: 0.3,
  cheer: 0.2,
  stepDone: 0.15,
};

const E6 = 1318.5;
const G6 = 1568;
const C7 = 2093;

const RECIPES: Record<Sfx, (c: AudioContext) => void> = {
  // 0.12 s soft wooden tap
  click: (c) => {
    tone(c, 1400, 0, 0.1, 'triangle', 0.08);
    noise(c, 0, 0.04, 3000, 2000, 0.03, 2);
  },
  // 0.39 s low "nope" buzz
  disabled: (c) => {
    tone(c, 180, 0, 0.36, 'square', 0.04, 120);
    tone(c, 90, 0, 0.3, 'sine', 0.08, 70);
  },
  // 0.13 s ratchet tick
  slider: (c) => {
    tone(c, 900, 0, 0.06, 'triangle', 0.05);
    tone(c, 1300, 0.05, 0.07, 'triangle', 0.035);
  },
  // 0.94 s paper slide + rising hum
  drawer: (c) => {
    noise(c, 0, 0.9, 400, 2400, 0.06);
    tone(c, 300, 0.05, 0.85, 'sine', 0.025, 600);
  },
  // 0.36 s air swoosh (transition)
  whoosh: (c) => noise(c, 0, 0.34, 2400, 500, 0.08),
  // 1.22 s semi-magical woosh: rising air + four bells
  magic: (c) => {
    noise(c, 0, 1.0, 600, 5000, 0.05);
    [C6, E6, G6, C7].forEach((f, i) => tone(c, f, 0.1 + i * 0.12, 0.62, 'sine', 0.05));
  },
  // 0.24 s two-step pop
  cardAdded: (c) => {
    tone(c, 700, 0, 0.09, 'triangle', 0.08);
    tone(c, 1050, 0.08, 0.15, 'triangle', 0.08);
  },
  // 2.20 s card highlight: bright triad with a shimmering tail
  highlight: (c) => {
    [E5, G5, C6].forEach((f, i) => tone(c, f, i * 0.09, 2.0, 'sine', 0.05));
    noise(c, 0.2, 1.6, 4000, 9000, 0.015, 3);
  },
  // 2.04 s ring filling: slow glide up then a ping
  circleFill: (c) => {
    tone(c, 300, 0, 1.85, 'sine', 0.06, 900);
    tone(c, 1800, 1.86, 0.17, 'triangle', 0.05);
  },
  // 1.10 s sparkle pop: nine twinkles
  sparkle: (c) => {
    tone(c, 600, 0, 0.12, 'triangle', 0.05, 1200);
    for (let i = 0; i < 9; i++) tone(c, 1800 + Math.random() * 1600, 0.05 + i * 0.09, 0.28, 'sine', 0.035);
  },
  // 2.21 s chime sweep: staggered triad held
  chime: (c) => [C5, E5, G5, C6].forEach((f, i) => tone(c, f, i * 0.05, 2.05, 'sine', 0.055)),
  // 1.80 s completion: arpeggio resolving to a chord
  complete: (c) => {
    [C5, E5, G5].forEach((f, i) => tone(c, f, i * 0.1, 0.35, 'triangle', 0.07));
    [C5, G5, C6].forEach((f) => tone(c, f, 0.32, 1.45, 'triangle', 0.05));
  },
  // 3.56 s big celebration: fanfare, sparkle shower, long chord
  celebrate: (c) => {
    [C5, E5, G5, C6, G5, C6].forEach((f, i) => tone(c, f, i * 0.11, 0.35, 'triangle', 0.08));
    noise(c, 0.6, 0.9, 3000, 8000, 0.04);
    [C5, E5, G5, C6].forEach((f) => tone(c, f, 0.8, 2.7, 'sine', 0.04));
  },
  // 3.08 s skill celebration appearance: rising bells + held chord
  skill: (c) => {
    [G5, C6, E6, G6].forEach((f, i) => tone(c, f, i * 0.15, 0.7, 'sine', 0.06));
    [C6, E6, G6].forEach((f) => tone(c, f, 0.7, 2.35, 'sine', 0.03));
  },
  // 2.20 s unlock: latch clack then an opening triad
  unlock: (c) => {
    tone(c, 400, 0, 0.08, 'square', 0.05);
    noise(c, 0, 0.06, 2500, 1500, 0.08, 1.5);
    [C5, G5, C6].forEach((f, i) => tone(c, f, 0.12 + i * 0.1, 1.85, 'sine', 0.06));
  },
  // 4.84 s unit unlocking sequence: build-up, ladder, glowing chord
  unitUnlock: (c) => {
    noise(c, 0, 1.6, 300, 3000, 0.05);
    [C5, E5, G5, C6, E6].forEach((f, i) => tone(c, f, 0.3 + i * 0.16, 0.8, 'sine', 0.06));
    [C5, E5, G5, C6].forEach((f) => tone(c, f, 1.3, 3.5, 'sine', 0.035));
  },
  // 1.63 s catch-up: falling notes then a rising "your turn"
  catchup: (c) => {
    [G5, E5, C5].forEach((f, i) => tone(c, f, i * 0.12, 0.35, 'sine', 0.07));
    noise(c, 0.35, 0.5, 800, 3000, 0.03);
    tone(c, E5, 0.5, 1.1, 'sine', 0.05, G5);
  },
  // 2.44 s reference library: page flutter and a warm chord
  refLibrary: (c) => {
    noise(c, 0, 0.5, 1200, 300, 0.05);
    tone(c, E5, 0.2, 0.6, 'sine', 0.06);
    [C5, E5, G5].forEach((f) => tone(c, f, 0.5, 1.9, 'sine', 0.03));
  },
  shutter: (c) => {
    noise(c, 0, 0.05, 4000, 3000, 0.25, 0.7);
    noise(c, 0.07, 0.06, 2500, 1800, 0.2, 0.7);
  },
  pageTurn: (c) => noise(c, 0, 0.35, 800, 3200, 0.07, 0.6),
  // 0.18 s soft bubble pop (Juno's speech bubble)
  pop: (c) => {
    tone(c, 420, 0, 0.14, 'sine', 0.09, 980);
    noise(c, 0, 0.03, 2600, 2000, 0.03, 3);
  },
  // 0.70 s Scribble's cheer: two happy chirps and a twinkle
  cheer: (c) => {
    tone(c, 880, 0, 0.16, 'triangle', 0.06, 1320);
    tone(c, 1046.5, 0.17, 0.2, 'triangle', 0.06, 1568);
    tone(c, 2093, 0.38, 0.3, 'sine', 0.03);
  },
  // 0.90 s paper confetti crackle
  confetti: (c) => {
    for (let i = 0; i < 10; i++) noise(c, i * 0.075 + Math.random() * 0.03, 0.08, 5200, 3600, 0.035, 2.5);
    tone(c, 1568, 0.05, 0.4, 'sine', 0.02);
  },
  // 0.62 s Juno says hi: a warm two-note bell
  junoHello: (c) => {
    tone(c, E5, 0, 0.3, 'sine', 0.06);
    tone(c, E5 * 2, 0, 0.18, 'sine', 0.012);
    tone(c, 880, 0.16, 0.44, 'sine', 0.06);
    tone(c, 1760, 0.16, 0.24, 'sine', 0.012);
  },
  // 0.50 s step done: marimba up a third
  stepDone: (c) => {
    tone(c, G5, 0, 0.22, 'sine', 0.07);
    tone(c, G5 * 4, 0, 0.05, 'sine', 0.012);
    tone(c, 987.77, 0.12, 0.36, 'sine', 0.07);
    tone(c, 987.77 * 4, 0.12, 0.05, 'sine', 0.012);
  },
};

/** Extra layers for the biggest moments: a soft sub "thump" and a high shimmer. */
const LAYERS: Partial<Record<Sfx, (c: AudioContext) => void>> = {
  celebrate: (c) => {
    tone(c, 110, 0, 0.5, 'sine', 0.12, 55);
    noise(c, 0.1, 2.4, 7000, 11000, 0.012, 4);
  },
  unitUnlock: (c) => tone(c, 98, 1.25, 0.6, 'sine', 0.1, 49),
  skill: (c) => noise(c, 0.2, 2.2, 6000, 10000, 0.01, 4),
};

export function play(s: Sfx): void {
  if (!useApp.getState().settings.sfx) return;
  const b = audioBuses();
  if (!b) return;
  const c = b.ctx;
  try {
    dest = b.sfx;
    const wet = WET[s] ?? 0;
    if (wet > 0) {
      const g = c.createGain();
      g.gain.value = wet;
      g.connect(b.room);
      wetSend = g;
    } else wetSend = null;
    RECIPES[s](c);
    LAYERS[s]?.(c);
  } catch {
    /* audio unavailable */
  } finally {
    dest = null;
    wetSend = null;
  }
}

/* --------------------------- Background music --------------------------- */

/** Settings → Music on/off. The music itself is generated per world in lib/music.ts. */
export function setMusic(on: boolean): void {
  setMusicEnabled(on);
}
